import { el, VNode } from "@dhx/ts-common/dom";
import { EventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { IKeyManager, KeyManager } from "@dhx/ts-common/KeyManager";
import { Id, IHandlers } from "@dhx/ts-common/types";
import { IDataItem } from "@dhx/ts-data";
import { ShapesCollection } from "../ShapesCollection";

export enum EditorEvents {
	beforeEditorOpen = "beforeEditorOpen",
	afterEditorOpen = "afterEditorOpen",
	beforeEditorClose = "beforeEditorClose",
	afterEditorClose = "afterEditorClose",
	beforeEditorEditing = "beforeEditorEditing",
	afterEditorEditing = "afterEditorEditing",
}

export interface IEditorEventHandlersMap {
	[key: string]: (...args: any[]) => any;

	[EditorEvents.beforeEditorOpen]: (id: Id, key: string, subId?: string) => boolean | void;
	[EditorEvents.afterEditorOpen]: (id: Id, key: string, subId?: string) => void;
	[EditorEvents.beforeEditorClose]: (id: Id, key: string, subId?: string) => boolean | void;
	[EditorEvents.afterEditorClose]: (id: Id, key: string, subId?: string) => void;
	[EditorEvents.beforeEditorEditing]: (
		value: string,
		currentValue: string,
		id: Id,
		key: string,
		subId?: string
	) => boolean | void;
	[EditorEvents.afterEditorEditing]: (value: string, id: Id, key: string, subId?: string) => void;
}

export interface IEditorConfig {
	data: ShapesCollection;
	events: EventSystem<EditorEvents, IEditorEventHandlersMap>;
}

export interface IEditor {
	data: ShapesCollection;
	events: EventSystem<EditorEvents, IEditorEventHandlersMap>;

	openEditor(id: Id, key?: string, subId?: string): boolean;
	closeEditor(): boolean;
	isEditable(): boolean;
	destructor(): void;
	render(): VNode;
}

export class Editor implements IEditor {
	public data: ShapesCollection;
	public events: EventSystem<EditorEvents, IEditorEventHandlersMap>;

	private _editable: boolean;
	private _editableItem: IDataItem;
	private _keyManager: IKeyManager;
	private _documentClick: (event: MouseEvent) => void;
	private _currentValue = " ";
	private _handlers: IHandlers;
	private _key: string;
	private _subId: string;

	constructor(config: IEditorConfig) {
		this.data = config.data;
		this.events = config.events;

		this._keyManager = new KeyManager();
		this._documentClick = (event: MouseEvent) => {
			if (!locate(event, "dhx_editor_id")) this.closeEditor();
		};
		this._handlers = {
			edit: event => {
				this.edit(event.target.innerText);
			},
			didInsert: (node: VNode) => {
				const element = node?.el;
				if (element?.childNodes[0]) {
					const range = document.createRange();
					const selection = window.getSelection();

					range.setStart(element.childNodes[0], element.innerText.length);
					range.collapse(true);

					selection.removeAllRanges();
					selection.addRange(range);
					element.focus();
				}
			},
		};
	}
	public openEditor(id: Id, key = "text", subId?: string): boolean {
		const item = this.data.getItem(id);

		if (!item || this._editable || !this.events.fire(EditorEvents.beforeEditorOpen, [id, key, subId])) {
			return false;
		}

		this._addHotkeys();
		this._initOuterClick();

		this.data.update(id, { $editable: true }, true);
		this._editableItem = item;
		this._editable = true;
		this._key = key;
		this._subId = subId;

		const type = this._editableItem.$item.getBaseType();

		if (type === "group" || type === "swimlane") {
			if (subId) {
				const { subHeaderRows, subHeaderCols } = this._editableItem;
				let subHeaderRow = [];
				if (subHeaderRows?.enable && subHeaderRows?.headers?.length) {
					subHeaderRow = subHeaderRows.headers;
				}
				let subHeaderCol = [];
				if (subHeaderCols?.enable && subHeaderCols?.headers?.length) {
					subHeaderCol = subHeaderCols.headers;
				}
				const subHeaders = [...subHeaderRow, ...subHeaderCol];
				const subHeader = subHeaders.find(header => header?.id === subId);

				this._currentValue = subHeader?.[key];
			} else {
				this._currentValue = this._editableItem.header[key];
			}
		} else if (type === "line" && item.$subSelected) {
			item.title.text.map(title => {
				if (title.id === item.$subSelected) {
					title.$editable = true;
					this._currentValue = title.text;
				}
				return title;
			});
		} else {
			this._currentValue = this._editableItem[key];
		}
		this.events.fire(EditorEvents.afterEditorOpen, [id, this._key, this._subId]);

		return true;
	}
	public closeEditor(): boolean {
		const { id } = this._editableItem;
		if (!this.events.fire(EditorEvents.beforeEditorClose, [id, this._key, this._subId])) return false;

		this._removeHotkeys();
		this._removeClickListener();

		this.data.update(id, { $editable: false }, true);

		if (this._editableItem.$item.getBaseType() === "line" && this._subId) {
			this._editableItem.title.text.map(title => {
				if (title.id === this._subId) {
					title.$editable = false;
				}
				return title;
			});
		}

		this._editable = false;
		this.events.fire(EditorEvents.afterEditorClose, [id, this._key, this._subId]);

		this._editableItem = null;
		this._subId = null;
		this._currentValue = " ";

		return true;
	}
	public isEditable(): boolean {
		return this._editable;
	}
	public destructor(): void {
		this._keyManager && this._keyManager.destructor();
		this.data = this.events = this._editable = this._editableItem = this._currentValue = null;
	}
	public render(): VNode {
		const { id } = this._editableItem;
		return el(
			"div.dhx_diagram_inline_editor",
			{
				dhx_editor_id: id + "_editor",
				contentEditable: true,
				oninput: this._handlers.edit,
				_hooks: {
					didInsert: this._handlers.didInsert,
				},
			},
			this._currentValue || " "
		);
	}
	protected edit(value: string): void {
		if (
			!this._editableItem ||
			!this.events.fire(EditorEvents.beforeEditorEditing, [
				value,
				this._currentValue,
				this._editableItem.id,
				this._key,
				this._subId,
			])
		) {
			return;
		}

		const { id, header, $item, subHeaderCols, subHeaderRows } = this._editableItem;

		this._currentValue = value;

		const type = $item.getBaseType();
		if (type === "group" || type === "swimlane") {
			if (this._subId) {
				const subHeaderRow = subHeaderRows?.headers?.findIndex(header => header?.id === this._subId);
				const subHeaderCol = subHeaderCols?.headers?.findIndex(header => header?.id === this._subId);
				if (subHeaderCol > -1) {
					const headers = [...subHeaderCols.headers];
					headers[subHeaderCol][this._key] = this._currentValue;

					this.data.update(id, {
						subHeaderCols: {
							...subHeaderCols,
							headers,
						},
					});
				}
				if (subHeaderRow > -1) {
					const headers = [...subHeaderRows.headers];
					headers[subHeaderRow][this._key] = this._currentValue;

					this.data.update(id, {
						subHeaderRows: {
							...subHeaderRows,
							headers,
						},
					});
				}
			} else {
				this.data.update(id, {
					header: {
						...header,
						[this._key]: this._currentValue,
					},
				});
			}
		} else if (type === "line" && this._subId) {
			this._editableItem.title.text.map(title => {
				if (title.id === this._subId) {
					title[this._key] = this._currentValue;
				}
				return title;
			});
			this.data.update(id, this._editableItem);
		} else {
			this.data.update(id, {
				[this._key]: this._currentValue,
			});
		}

		this.events.fire(EditorEvents.afterEditorEditing, [this._currentValue, id, this._key, this._subId]);
	}
	private _addHotkeys() {
		this._keyManager.addHotKey("escape", () => {
			this.closeEditor();
		});
	}
	private _removeHotkeys() {
		this._keyManager.removeHotKey();
	}
	private _initOuterClick() {
		document.addEventListener("click", this._documentClick);
	}
	private _removeClickListener() {
		document.removeEventListener("click", this._documentClick);
	}
}
